<script lang="ts">
// Module scope (a `<script setup>` top level runs once per instance). Websites
// whose whole provider chain already failed this page session. Lists re-mount
// hundreds of logos on every search / sort / view toggle; without this each
// re-mount walks the chain again and floods the console with 404s.
const failedWebsites = new Set<string>()
</script>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { buildLogoFallbacks } from '../utils/supplierLogo'

const props = defineProps<{
  src?: string | null
  /** Company website/domain — used to resolve a logo when `src` is missing or fails. */
  website?: string | null
  alt?: string
  class?: string
}>()

const currentSrc = ref<string | null>(null)
const showInitials = ref(false)
let fallbackAttempt = 0
let fallbackUrls: string[] = []

const initials = (() => {
  const name = props.alt || ''
  return name.slice(0, 2).toUpperCase() || '?'
})()

const loadImage = (url: string | null | undefined) => {
  fallbackAttempt = 0
  showInitials.value = false
  // Even with no logo URL, a supplier website can still resolve a logo via the
  // provider chain — only show initials when we have neither a URL nor a website.
  fallbackUrls = buildLogoFallbacks(url, props.website)
  if (fallbackUrls.length === 0 || (props.website && failedWebsites.has(props.website))) {
    showInitials.value = true
    currentSrc.value = null
    return
  }
  currentSrc.value = fallbackUrls[0] ?? null
}

const onError = () => {
  fallbackAttempt++
  if (fallbackAttempt < fallbackUrls.length) {
    currentSrc.value = fallbackUrls[fallbackAttempt] ?? null
  } else {
    currentSrc.value = null
    showInitials.value = true
    if (props.website) failedWebsites.add(props.website)
  }
}

// Google s2 answers unknown domains with HTTP 404 *and* a 16x16 globe PNG. Browsers
// decode the body and fire `load`, not `error`, so the chain would stop on the globe.
// Treat any tiny image as a miss and advance. (0 = jsdom/no dimensions: leave it.)
const onLoad = (e: Event) => {
  const img = e.target as HTMLImageElement
  if (img.naturalWidth > 0 && img.naturalWidth <= 16) onError()
}

watch(() => props.src, (newVal) => {
  loadImage(newVal)
})

onMounted(() => {
  loadImage(props.src)
})
</script>

<template>
  <img
    v-if="!showInitials && currentSrc"
    :src="currentSrc"
    :alt="alt || 'Supplier Logo'"
    :class="props.class"
    loading="lazy"
    @error="onError"
    @load="onLoad"
  />
  <div
    v-else
    :class="props.class"
    class="bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 font-bold select-none"
    :title="alt"
  >
    {{ initials }}
  </div>
</template>
